import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@foris/avocado-suite";
import type { ProposalsContext } from "@services/collections/assignment-proposal/assignmentProposalServices";
import type { ProposalIntent } from "@services/types/assignment-proposal/assignmentProposalTypes";
import { outcomeErrorKey } from "@utils/assignmentProposal";

type CardActionVariables = ProposalsContext & { linkId: string; proposalId: string };

interface ActionOutcome {
  kind: string;
  errorCode?: string | null;
}

interface ProposalMutation<TVariables, TOutcome> {
  submit: (variables: TVariables) => Promise<TOutcome | null>;
  isSubmitting: boolean;
}

interface ProposalCardActionOptions<TOutcome extends ActionOutcome> extends ProposalsContext {
  linkId: string | null;
  /** The outcome of an action that went through. */
  successKind: TOutcome["kind"];
  /** i18n key of the toast once it went through. */
  successKey: string;
  intent: ProposalIntent;
  /** Requests again the list the action changed. */
  reload: () => void;
  /** Error codes that mean the list behind the modal is out of date. */
  reloadOn?: readonly string[];
}

/**
 * An action a card of the proposals Drawer confirms in a modal (reject, delete): the proposal it is
 * open for, its request and its message. Once it goes through, the modal closes, the toast shows and
 * the list is requested again; a failure keeps the modal open with the mapped message.
 */
export const useProposalCardAction = <
  TVariables extends CardActionVariables,
  TOutcome extends ActionOutcome,
>(
  mutation: ProposalMutation<TVariables, TOutcome>,
  {
    scenarioId,
    originId,
    linkId,
    successKind,
    successKey,
    intent,
    reload,
    reloadOn = [],
  }: ProposalCardActionOptions<TOutcome>,
) => {
  const { t } = useTranslation();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const open = useCallback((proposalId: string) => {
    setError(null);
    setTargetId(proposalId);
  }, []);

  const close = useCallback(() => setTargetId(null), []);

  const confirm = async (input: Omit<TVariables, keyof CardActionVariables>) => {
    if (!targetId || !linkId) return;

    setError(null);
    const outcome = await mutation.submit({
      ...input,
      scenarioId,
      originId,
      linkId,
      proposalId: targetId,
    } as TVariables);

    // A call made while another one runs is ignored by the mutation.
    if (!outcome) return;

    if (outcome.kind === successKind) {
      setTargetId(null);
      toast.success(t(successKey), { autoClose: 3000 });
      reload();
      return;
    }

    setError(t(outcomeErrorKey(outcome, intent)));
    // The modal keeps the message; the list behind it catches up.
    if (outcome.errorCode && reloadOn.includes(outcome.errorCode)) reload();
  };

  return { targetId, error, isSubmitting: mutation.isSubmitting, open, close, confirm };
};
